/**
 * Synchronises Redux state with query string.
 *
 * Example usage in a route component connected to Redux:
 * ```ts
 * import { UrlQueryParamsFeatureActions } from 'store/features/url-query-params/actions';
 *
 * // in component
 * componentDidMount() {
 *   this.props.onMount();
 * }
 *
 * componentWillUnmount() {
 *   this.props.onUnmount();
 * }
 *
 * // in mapDispatchToProps
 * onMount: () {
 *   dispatch(
 *     UrlQueryParamsFeatureActions.registerView({ ... });
 *   );
 * },
 * onUnmount: () => {
 *   dispatch(UrlQueryParamsFeatureActions.unregisterView({ ... }));
 * }
 * ```
 *
 * registerView method requires the following params:
 * - `view`: one of UrlQueryParamsView enum values
 * - `setQueryParamsAction`: action that will be dispatched when query params have changed
 *   and Redux store must be synchronised with it. Note that you must implement the reducer
 *   that handles this store change yourself.
 * - `queryStringSerializer`: function that serializes current Redux store state to a query string
 * - `queryStringParser`: function that parsers query string object and fills in the `setQueryParamsAction` payload
 */
import { Action } from "redux";
import { SagaIterator, eventChannel } from "redux-saga";
import { all, call, cancel, fork, put, select, take, takeEvery } from "redux-saga/effects";

import { currentUtmParams } from "helpers/sagas";
import { parseQueryParams } from "helpers/url";
import { BrowserHistory, IBrowserHistory } from "services/browser-history";
import { IActionWithPayload } from "store/helper";

import { UrlQueryParamsFeatureActionNames } from "./actions";
import {
  IUrlQueryParamsFeatureRegisterViewPayload,
  IUrlQueryParamsFeatureUnregisterViewPayload,
  IViewRegistry,
  IViewRegistryItem,
  IWithViewQueryParams,
  UpdateQueryTrigger,
} from "./interfaces";

const queryStringBuilders = {
  // [UrlQueryParamsView.Archives]: buildArchivesViewQueryString
};

const viewRegistry: IViewRegistry = {};

function registeredQueryStringSerializers(): IViewRegistryItem[] {
  return Object.values(viewRegistry);
}

/**
 * Builds a query string from all registered views based on the state of
 * redux store. All results are joined using `&`.
 * @desc If two builders provide `tag[]=spam` and `rate=rated_good` results,
 * it will return `tag[]=spam&rate=rated_good`.
 * @return {string} Query string based on redux store state.
 */
function buildQueryString(state: IWithViewQueryParams, pathname: string): string {
  return registeredQueryStringSerializers()
    .map(registryItem => registryItem.queryStringSerializer(state, pathname))
    .concat([currentUtmParams()])
    .filter(Boolean)
    .join("&");
}

function shouldActionReplaceHistory(action: Action): boolean {
  return registeredQueryStringSerializers().some(registryItem => {
    switch (typeof registryItem.replaceHistoryActions) {
      case "string":
        return registryItem.replaceHistoryActions === action.type;
      case "object":
        return Array.isArray(registryItem.replaceHistoryActions)
          ? registryItem.replaceHistoryActions.includes(action.type)
          : null;
      default:
        return null;
    }
  });
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* updateUrlQueryParams(action: any): SagaIterator {
  if (registeredQueryStringSerializers().length === 0) {
    return;
  }

  const state = yield select();
  const newQueryString = buildQueryString(state, BrowserHistory.pathname);

  if (newQueryString !== BrowserHistory.queryString) {
    const replace =
      action.type === UrlQueryParamsFeatureActionNames.REGISTER_VIEW || shouldActionReplaceHistory(action);
    if (replace) {
      BrowserHistory.replaceQueryString(newQueryString);
    } else {
      BrowserHistory.pushQueryString(newQueryString);
    }
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* registerView(
  action: IActionWithPayload<UrlQueryParamsFeatureActionNames, IUrlQueryParamsFeatureRegisterViewPayload>
) {
  const {
    view,
    updateQueryActions,
    replaceHistoryActions,
    setQueryParamsAction,
    queryStringSerializer,
    queryStringParser,
    updateQueryTrigger,
    shouldSetQueryParamsOnRegister,
    isPathMatchingPattern,
  } = action.payload;

  viewRegistry[view] = {
    // @ts-expect-error TS(2322): Type '((payload: { params: IKeyValue<string>; }) =... Remove this comment to see the full error message
    setQueryParamsAction,
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    queryStringSerializer: queryStringSerializer || queryStringBuilders[view],
    // @ts-expect-error TS(2322): Type '((params: IKeyValue<string>, pathname?: stri... Remove this comment to see the full error message
    queryStringParser,
    updateQueryActions,
    replaceHistoryActions,
    updateQueryTrigger,
    isPathMatchingPattern,
    updateUrlQueryParams: yield takeEvery(updateQueryActions || "*", updateUrlQueryParams),
  };

  if (setQueryParamsAction) {
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const state = yield select();
    const queryStringFromStore = viewRegistry[view].queryStringSerializer(state);
    // We have to check if user comes from different in-app navigation that contain query params. E.g: Raports -> Archives
    if (
      !BrowserHistory.hasLocationEverChanged ||
      shouldSetQueryParamsOnRegister ||
      (BrowserHistory.queryString.length > 0 && queryStringFromStore !== BrowserHistory.queryString)
    ) {
      yield put(
        // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        setQueryParamsAction({ params: queryStringParser(BrowserHistory.queryParams, BrowserHistory.pathname) })
      );
    }
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* unregisterView(
  action: IActionWithPayload<UrlQueryParamsFeatureActionNames, IUrlQueryParamsFeatureUnregisterViewPayload>
) {
  const { view } = action.payload;
  yield cancel(viewRegistry[view].updateUrlQueryParams);
  delete viewRegistry[view];
}

/**
 * Creates {@link https://redux-saga.js.org/docs/advanced/Channels.html | eventChannel}
 * that lets a saga listen to popState browser history events
 */
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function createHistoryPopStateChannel(history: IBrowserHistory) {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return eventChannel((emit: any) => {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const unlisten = history.listen((location: any, action: any) => {
      if (action === "POP") {
        emit({ location });
      }
    });
    return unlisten;
  });
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* listenToHistoryPopState() {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const historyChannel = yield call(createHistoryPopStateChannel, BrowserHistory.browserHistoryObject);
  while (true) {
    const { location } = yield take(historyChannel);
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const state = yield select();

    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    const actions = yield registeredQueryStringSerializers().reduce((acc, registryItem) => {
      const queryStringFromStore = registryItem.queryStringSerializer(state);
      if (
        registryItem.updateQueryTrigger === UpdateQueryTrigger.QueryParamsChange &&
        queryStringFromStore === BrowserHistory.queryString
      ) {
        return acc;
      }

      if (!registryItem.isPathMatchingPattern || registryItem.isPathMatchingPattern(location.pathname)) {
        acc.push(
          // @ts-expect-error TS(2345): Argument of type 'any' is not assignable to parame... Remove this comment to see the full error message
          put(
            registryItem.setQueryParamsAction({
              params: registryItem.queryStringParser(parseQueryParams(location.search), location.pathname),
            })
          )
        );
      }

      return acc;
    }, []);

    yield all(actions);
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function* urlQueryParamsFeatureSaga() {
  yield takeEvery(UrlQueryParamsFeatureActionNames.REGISTER_VIEW, registerView);
  yield takeEvery(UrlQueryParamsFeatureActionNames.UNREGISTER_VIEW, unregisterView);
  yield fork(listenToHistoryPopState);
}
